import React, { useState, useEffect, useReducer } from 'react';
import styled from 'styled-components';
import { visibleScrollbar } from '../../../styledMixins';
import ScrollableArea from '../../Shared/ScrollableArea';
import { useHistory } from 'react-router-dom';
import Eye from '../../../static/icons/Eye.svg';
import MarkEdit from '../../../static/img/markEdit.svg';
import EditHover from '../../../static/img/EditHover.svg';
import CommentBankDialog from '../../Shared/Dialogs/commentBank';
import { isTabletView } from '../../ReactiveRender';
import MenuButton from '../../MenuButton';
import ImprovedSecondarySideBar from '../../ImprovedSecondarySideBar';
import ItemSettingsModal from '../../Shared/ItemSettingsModal';
import SettingsTagsDisplay from '../../Shared/SettingsTagsDisplay';
import StepLabelBadge from '../../Shared/StepLabelBadge';
import SetupStepSection from '../../Shared/SetupStepSection';
import FeedbackArea from '../FeedbackArea';
import { useAvailableYears, useSubjects, useSubjectsForYearFromClasses, useSubjectsForYearsFromClasses, useUserStateSubjects, useTaskTypes, useResponseTypes } from '../../../hooks/useSubjects';
import { useUserStates, useMarkingToolStates } from '../../../hooks/useUserStates';
import { withSelectedSubjectsPreserved } from '../../../utils/subjects';
import { TextInput, HeadingAndFilterContainer } from '../../CreateNewMarkingCriteria/CreateNewMarkingCriteriaDesktop/style';

function CreateNewCommentBankDesktop(props) {
  const {
    commentBank,
    updateCommentBank,
    UpdateSmartAnotationHandler,
    deleteAnnotationHandler,
    createSmartAnnotationHandler,
    reorderSmartCommentsHandler,
    saveCommentBank,
    handleTitleChange,
    isUpdating,
    isSaving = false,
    commentBankId,
  } = props;

  const history = useHistory();
  const { states: userStates } = useUserStates();
  const state = userStates;

  const filterReducer = (filterState, action) => {
    switch (action.type) {
      case 'SET_YEARS':
        return action.value?.length
          ? { ...filterState, years: action.value }
          : { years: action.value, subjects: [], taskTypes: [], responseTypes: [] };
      case 'SET_SUBJECTS':
        return { ...filterState, subjects: action.value };
      case 'SET_TASK_TYPES':
        return { ...filterState, taskTypes: action.value };
      case 'SET_RESPONSE_TYPES':
        return { ...filterState, responseTypes: action.value };
      case 'PRUNE_INVALID':
        return { ...filterState, ...action.updates };
      default:
        return filterState;
    }
  };

  const [filterState, dispatch] = useReducer(filterReducer, {
    years: null,
    subjects: null,
    taskTypes: null,
    responseTypes: null,
  });

  const meta = commentBank?.metadata || {};
  const selectedYears = filterState.years !== null ? filterState.years : (meta.years?.length ? meta.years : (commentBank?.years || []));
  const rawItemSubjects = meta.subjects?.length ? meta.subjects : (commentBank?.subjects || []);
  const normalizedItemSubjects = rawItemSubjects.map(s => (typeof s === 'object' && s !== null) ? (s.subjectCode || s.code || s.value || s) : s).filter(Boolean);
  const selectedSubjects = filterState.subjects !== null ? filterState.subjects : normalizedItemSubjects;
  const selectedTaskTypes = filterState.taskTypes !== null ? filterState.taskTypes : (commentBank?.taskTypes || []);
  const selectedResponseTypes = filterState.responseTypes !== null ? filterState.responseTypes : (commentBank?.responseTypes || []);

  const { years: availableYears } = useAvailableYears();
  const yearForFilter = selectedYears?.find((y) => availableYears?.includes(y)) ?? selectedYears?.[0];
  const yearsForFilter = Array.isArray(selectedYears) && selectedYears.length ? selectedYears : (yearForFilter != null ? [yearForFilter] : []);
  const scopeStates = useMarkingToolStates();
  const { subjects: subjectsByYear } = useSubjects(state, yearForFilter);
  const { subjects: subjectsFromClasses } = useSubjectsForYearFromClasses(yearForFilter, scopeStates);
  const { subjects: subjectsForSelectedYears } = useSubjectsForYearsFromClasses(selectedYears, scopeStates);
  const { subjects: allClassSubjects } = useUserStateSubjects(scopeStates);
  const subjectsForYear = (subjectsByYear?.length ? subjectsByYear : subjectsFromClasses) || [];
  const subjectsSource = selectedYears?.length
    ? (subjectsForSelectedYears?.length ? subjectsForSelectedYears : allClassSubjects)
    : [];
  const subjectsEnriched = (subjectsSource.length ? subjectsSource : subjectsForYear).map((s) => {
    const fromClass = (subjectsForSelectedYears?.length ? subjectsForSelectedYears : subjectsFromClasses)?.find((c) => c.code === s.code);
    return fromClass ? { ...s, taskTypes: fromClass.taskTypes || [], responseTypes: fromClass.responseTypes || [] } : s;
  });
  const { taskTypes: filteredTaskTypes } = useTaskTypes(selectedSubjects, state, yearsForFilter.length ? yearsForFilter : yearForFilter);
  const { responseTypes: filteredResponseTypes } = useResponseTypes(selectedSubjects, selectedTaskTypes, state, yearsForFilter.length ? yearsForFilter : yearForFilter);

  // Keep an edited tool's existing (possibly out-of-state) subject in the
  // options so state scoping can't silently strip it on save.
  const availableSubjects = withSelectedSubjectsPreserved(
    selectedYears?.length ? subjectsEnriched : [],
    selectedSubjects,
    rawItemSubjects,
  );
  const availableTaskTypes = selectedSubjects.length ? filteredTaskTypes : [];
  const availableResponseTypes = (selectedSubjects.length && selectedTaskTypes.length) ? filteredResponseTypes : [];

  // Single effect to prune invalid task types and response types together
  useEffect(() => {
    const updates = {};
    if (selectedTaskTypes.length) {
      if (!availableTaskTypes.length) {
        updates.taskTypes = [];
      } else {
        const validTaskValues = new Set(availableTaskTypes.map(t => t.value ?? t));
        const filteredTasks = selectedTaskTypes.filter(t => validTaskValues.has(t));
        if (filteredTasks.length !== selectedTaskTypes.length) updates.taskTypes = filteredTasks;
      }
    }
    if (selectedResponseTypes.length) {
      if (!availableResponseTypes.length) {
        updates.responseTypes = [];
      } else {
        const validRespValues = new Set(availableResponseTypes.map(r => r.value ?? r));
        const filteredResp = selectedResponseTypes.filter(r => validRespValues.has(r));
        if (filteredResp.length !== selectedResponseTypes.length) updates.responseTypes = filteredResp;
      }
    }
    if (Object.keys(updates).length) dispatch({ type: 'PRUNE_INVALID', updates });
  }, [availableTaskTypes, availableResponseTypes]);

  const onYearsChange = (v) => dispatch({ type: 'SET_YEARS', value: v });
  const onSubjectsChange = (v) => dispatch({ type: 'SET_SUBJECTS', value: v });
  const onTaskTypesChange = (v) => dispatch({ type: 'SET_TASK_TYPES', value: v });

  // Setter aliases for compatibility with child component props and toggle handlers
  const setLocalYears = (v) => dispatch({ type: 'SET_YEARS', value: v });
  const setLocalSubjects = (v) => dispatch({ type: 'SET_SUBJECTS', value: v });
  const setLocalTaskTypes = (v) => dispatch({ type: 'SET_TASK_TYPES', value: v });
  const setLocalResponseTypes = (v) => dispatch({ type: 'SET_RESPONSE_TYPES', value: v });

  const [isEditing, setIsEditing] = useState(false);
  const [openPreviewDialog, setOpenPreviewDialog] = useState(false);
  const [isShowMenu, setShowMenu] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  const tabletView = isTabletView();

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      setIsEditing(false);
    }
  };

  const handleOnBlur = () => {
    setIsEditing(false);
  };

  const handleYearToggle = (year) => {
    setLocalYears(selectedYears.includes(year) ? selectedYears.filter((y) => y !== year) : [...selectedYears, year]);
  };

  // Helper to get subject code (handles both object and string formats)
  const getSubjectCode = (s) => {
    if (typeof s === 'object') return s.subjectCode || s.code || s;
    return s;
  };

  // Helper to format subject for display
  const formatSubjectForDisplay = (s) => {
    if (typeof s === 'object') return s.subjectName || s.name || s.title || s.subjectCode || s.code || '';
    return s;
  };

  // Helper to check if subject is in selected subjects (handles both object and string formats)
  const isSelectedSubject = (subject, selected) => {
    const subjectCode = getSubjectCode(subject);
    return selected.some(s => {
      const selectedCode = getSubjectCode(s);
      return selectedCode === subjectCode;
    });
  };

  const handleSubjectToggle = (subject) => {
    const subjectCode = getSubjectCode(subject);
    if (isSelectedSubject(subject, selectedSubjects)) {
      setLocalSubjects(selectedSubjects.filter(s => getSubjectCode(s) !== subjectCode));
    } else {
      setLocalSubjects([...selectedSubjects, subject]);
    }
  };

  const handleTaskTypeToggle = (type) => {
    setLocalTaskTypes(selectedTaskTypes.includes(type) ? selectedTaskTypes.filter((t) => t !== type) : [...selectedTaskTypes, type]);
  };

  const handleResponseTypeToggle = (type) => {
    setLocalResponseTypes(selectedResponseTypes.includes(type) ? selectedResponseTypes.filter((r) => r !== type) : [...selectedResponseTypes, type]);
  };

  const handleGoBack = () => {
    history.push('/tools/commentbanks');
  };

  const canSave = selectedYears.length > 0 && selectedSubjects.length > 0;

  const handleSave = () => {
    if (!canSave || isSaving) return;
    saveCommentBank({
      years: selectedYears,
      subjects: selectedSubjects,
      taskTypes: selectedTaskTypes,
      responseTypes: selectedResponseTypes,
    });
  };

  const handleSaveSettings = async (settings) => {
    try {
      // Convert subjects to API format: { subjectCode, subjectName }
      const subjectObjects = (settings.subjects || []).map(s => {
        if (typeof s === 'object' && s !== null) {
          // If already has subjectCode/subjectName, return as-is
          if (s.subjectCode && s.subjectName) {
            return s;
          }
          // Convert {code, title} or {value, label} to {subjectCode, subjectName}
          return {
            subjectCode: s.subjectCode || s.code || s.value,
            subjectName: s.subjectName || s.name || s.title || s.label
          };
        }
        // If it's a string/code, look up the full subject object
        const found = availableSubjects.find(subj => getSubjectCode(subj) === s);
        if (found) {
          return {
            subjectCode: found.code || found.subjectCode || found.value,
            subjectName: found.title || found.subjectName || found.name || found.label
          };
        }
        return null;
      }).filter(Boolean);

      await updateCommentBank({
        years: settings.years,
        subjects: subjectObjects,
        taskTypes: settings.taskTypes,
        responseTypes: settings.responseTypes,
      });
      setLocalYears(settings.years || []);
      setLocalSubjects(subjectObjects);
      setLocalTaskTypes(settings.taskTypes || []);
      setLocalResponseTypes(settings.responseTypes || []);
    } catch (error) {
      console.error('Error saving settings:', error);
    }
  };

  return (
    <>
      <MainContainer>
        <ImprovedSecondarySideBar
          isShowMenu={isShowMenu}
          setShowMenu={setShowMenu}
          id={commentBankId}
        />
        <ContentWrapper>
          <InnerContainer>
            {tabletView && (
              <HeadingAndFilterContainer>
                <MenuButton setShowMenu={setShowMenu} />
              </HeadingAndFilterContainer>
            )}
            <RightContainer>
              <HeadingContainer>
                <HeadingLeft>
                  {isUpdating && (
                    <BackArrow onClick={handleGoBack}>&lt;</BackArrow>
                  )}
                  {isEditing ? (
                    <Heading id="commentBankTitleContainer">
                      <TextInput
                        placeholder="Name of comment bank"
                        id="commentBankName"
                        value={commentBank.title}
                        onChange={handleTitleChange}
                        onBlur={() => handleOnBlur()}
                        onKeyPress={handleKeyPress}
                        maxLength="100"
                      />
                    </Heading>
                  ) : (
                    <Heading onClick={() => setIsEditing(true)}>
                      {commentBank.title || 'Title'}
                      <EditIcon src={MarkEdit} />
                      <EditIconHover src={EditHover} />
                    </Heading>
                  )}
                </HeadingLeft>
                {!isUpdating && (
                  <HeaderRight>
                    <PreviewButton onClick={() => setOpenPreviewDialog(true)}>
                      <PreviewButtonIcon src={Eye} />
                      <PreviewButtonText>Preview</PreviewButtonText>
                    </PreviewButton>
                    <SaveButton onClick={handleSave} $disabled={!canSave || isSaving}>
                      <SaveButtonText>Save and Publish</SaveButtonText>
                    </SaveButton>
                  </HeaderRight>
                )}
              </HeadingContainer>
              {isUpdating && (
                <SettingsTagsRow>
                  <SettingsTagsDisplay
                    years={selectedYears}
                    subjects={selectedSubjects}
                    taskTypes={selectedTaskTypes}
                    responseTypes={selectedResponseTypes}
                    taskTypeLabels={Object.fromEntries((availableTaskTypes ?? []).map(t => [(t && typeof t === 'object' ? t.value : t), (t && typeof t === 'object' ? t.label ?? t.title ?? t.value : t)]))}
                    responseTypeLabels={Object.fromEntries((availableResponseTypes ?? []).map(r => [(r && typeof r === 'object' ? r.value : r), (r && typeof r === 'object' ? r.label ?? r.title ?? r.value : r)]))}
                    onSettingsClick={() => setIsSettingsOpen(!isSettingsOpen)}
                    showSettingsButton={true}
                  />
                </SettingsTagsRow>
              )}

              <MainContentSection>
              {/* Step 1 - Set Up Section (ONLY for create mode) */}
              {!isUpdating && (
                <SetupStepSection
                  availableYears={availableYears}
                  availableSubjects={availableSubjects}
                  availableTaskTypes={availableTaskTypes}
                  availableResponseTypes={availableResponseTypes}
                  selectedYears={selectedYears}
                  selectedSubjects={selectedSubjects}
                  selectedTaskTypes={selectedTaskTypes}
                  selectedResponseTypes={selectedResponseTypes}
                  onYearsChange={onYearsChange}
                  onSubjectsChange={onSubjectsChange}
                  onTaskTypesChange={onTaskTypesChange}
                  onResponseTypesChange={setLocalResponseTypes}
                  getSubjectLabel={formatSubjectForDisplay}
                  getSubjectValue={getSubjectCode}
                  isUpdating={isUpdating}
                />
              )}

              {/* Step 2 - Add Comment Bank Section (ONLY for create mode) */}
              {!isUpdating && (
                <StepLabelRow>
                  <StepLabelBadge>Step 2 - Add Comment Bank</StepLabelBadge>
                </StepLabelRow>
              )}

              {/* Feedback Areas Content */}
              <FeedbackAreaContainer>
                <FeedbackArea
                  smartAnnotation={commentBank}
                  UpdateSmartAnotationHandler={UpdateSmartAnotationHandler}
                  deleteAnnotationHandler={deleteAnnotationHandler}
                  createSmartAnnotationHandler={createSmartAnnotationHandler}
                  reorderSmartCommentsHandler={reorderSmartCommentsHandler}
                  smartAnnotationeditIndex={0}
                />
              </FeedbackAreaContainer>

              {/* Bottom Buttons */}
              <BottomButtonsContainer>
                <PreviewButton onClick={() => setOpenPreviewDialog(true)}>
                  <PreviewButtonIcon src={Eye} />
                  <PreviewButtonText>Preview</PreviewButtonText>
                </PreviewButton>
                <SaveButton onClick={handleSave} $disabled={!canSave || isSaving}>
                  <SaveButtonText>Save and Publish</SaveButtonText>
                </SaveButton>
              </BottomButtonsContainer>
              </MainContentSection>
            </RightContainer>
          </InnerContainer>

          {/* Settings Modal */}
          <ItemSettingsModal
            isOpen={isSettingsOpen}
            onClose={() => {
              setIsSettingsOpen(false);
              setLocalYears(null);
              setLocalSubjects(null);
              setLocalTaskTypes(null);
              setLocalResponseTypes(null);
            }}
            onSave={handleSaveSettings}
            availableYears={availableYears}
            availableSubjects={availableSubjects.map(s => ({ value: getSubjectCode(s), label: formatSubjectForDisplay(s) }))}
            taskTypes={availableTaskTypes}
            responseTypes={availableResponseTypes}
            selectedYears={selectedYears}
            setSelectedYears={onYearsChange}
            selectedSubjects={selectedSubjects.map(getSubjectCode)}
            setSelectedSubjects={(values) => onSubjectsChange(values.map(v => {
              const found = availableSubjects.find(s => getSubjectCode(s) === v);
              return found || v;
            }))}
            selectedTaskTypes={selectedTaskTypes}
            setSelectedTaskTypes={onTaskTypesChange}
            selectedResponseTypes={selectedResponseTypes}
            setSelectedResponseTypes={setLocalResponseTypes}
          />
        </ContentWrapper>
      </MainContainer>

      {openPreviewDialog && (
        <CommentBankDialog
          setCommentBankPreviewDialog={setOpenPreviewDialog}
          commentBank={commentBank}
        />
      )}
    </>
  );
}

// Styled Components
const MainContainer = styled.div`
  width: 100%;
  position: relative;
  align-items: flex-start;
  background-color: var(--white);
  display: flex;
  flex-direction: row;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  height: 100vh;
`;

const InnerContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 100vh;
  position: relative;
`;

const RightContainer = styled(ScrollableArea).attrs({ autoHide: 'leave' })`
  width: 100%;
  background: #ffffff;
  height: 100vh;
`;

const HeadingContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 16px 0;
  border-bottom: 1px solid #c9c6cc80;
  background: #ffffff;
`;

const HeadingLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
  margin-right: 8px;
`;

const BackArrow = styled.span`
  font-family: IBM Plex Sans;
  font-size: 20px;
  font-weight: 400;
  color: #7200e0;
  cursor: pointer;
  padding: 4px 8px;

  &:hover {
    opacity: 0.9;
  }
`;

const Heading = styled.p`
  font-family: IBM Plex Sans;
  font-size: 16px;
  font-weight: 500;
  line-height: 21px;
  letter-spacing: -0.01em;
  text-align: left;
  display: flex;
  align-items: center;
  padding: 8px 6px;
  gap: 4px;
  cursor: pointer;
  border: 1px solid transparent;
  margin: 0;
  flex: 1;
  min-width: 0;

  &:hover {
    border: 1px solid #c9c6cc80;
  }
`;

const EditIcon = styled.img`
  width: 20px;
  height: 20px;
  opacity: 0.85;
  flex-shrink: 0;
`;

const EditIconHover = styled.img`
  width: 20px;
  height: 20px;
  display: none;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const SettingsTagsRow = styled.div`
  padding: 12px 24px 16px;
  border-bottom: 1px solid #c9c6cc80;
  background: #FBF7FE;
  border-radius: 0;
`;

const MainContentSection = styled.div`
  padding: 0 24px;
`;

const PreviewButton = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 12px;
  border-radius: 32px;
  border: 1px solid #7200e0;
  gap: 4px;
  background: #ffffff;
  cursor: pointer;
  height: 40px;

  &:hover {
    background-color: #f9f5ff;
  }
`;

const PreviewButtonIcon = styled.img`
  width: 20px;
  height: 20px;
`;

const PreviewButtonText = styled.p`
  font-family: IBM Plex Sans;
  font-size: 14px;
  font-weight: 500;
  line-height: 24px;
  color: #7200e0;
  margin: 0;
`;

const SaveButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  padding: 8px 16px;
  border-radius: 32px;
  background: #7200e0;
  cursor: pointer;

  &:hover {
    background-color: rgba(80, 0, 157, 1);
  }

  ${({ $disabled }) =>
    $disabled &&
    `
    background: #c9c6cc;
    cursor: not-allowed;
    pointer-events: none;
  `}
`;

const SaveButtonText = styled.p`
  font-family: IBM Plex Sans;
  font-size: 14px;
  font-weight: 500;
  line-height: 24px;
  color: #ffffff;
  margin: 0;
`;

// Step Section Styles
const StepLabelRow = styled.div`
  padding: 16px 0;
  background: #ffffff;
`;

const FeedbackAreaContainer = styled.div`
  padding: 0;
  background: #ffffff;
`;

const BottomButtonsContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
  padding: 20px 0;
  background: #ffffff;
  border-top: 1px solid #e5e5e5;
`;

export default CreateNewCommentBankDesktop;
